<script setup lang="ts">
import { onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useTagManagementStore } from "@/stores/tagManagement";
import { plus_small } from "pn-design-assets/pn-assets/icons";
import CategoryCard from "@/components/tagManagement/CategoryCard.vue";
import AddCategoryModal from "@/components/tagManagement/AddCategoryModal.vue";

const tagManagementStore = useTagManagementStore();
const { themes, notificationObj } = storeToRefs(tagManagementStore);

const activeTabId = ref<string | null>(null);
const showAddCategoryModal = ref(false);
const selectedThemeId = ref<number>(0);
const showSpinner = ref(false);

const selectTab = (id: string) => {
  activeTabId.value = id;
};

const openAddCategoryModal = (themeId: number) => {
  selectedThemeId.value = themeId;
  showAddCategoryModal.value = true;
};

onMounted(async () => {
  try {
    showSpinner.value = true;
    await tagManagementStore.getTagTree();
    if (themes.value.length > 0) {
      activeTabId.value = themes.value[0].themeId.toString();
    }
  } finally {
    showSpinner.value = false;
  }
});
</script>

<template>
  <div class="tag-management-view">
    <pn-toast
      class="bottom-toast"
      :appearance="notificationObj.status"
      v-show="notificationObj.show"
      :text="notificationObj.message"
      temporary
    ></pn-toast>
    <header class="flex flex-direction-column gap-16">
      <h1 class="pn-xl margin-0">Tag management</h1>
      <p class="pn-m margin-0">Create and manage categories and tags.</p>
    </header>
    <div :class="[{ spinner: showSpinner }]">
      <pn-spinner size="3" v-show="showSpinner" />
    </div>
    <pn-tablist :value="activeTabId" label="Tag management categories">
      <pn-tab
        v-for="theme in themes"
        :key="theme.themeId"
        :label="theme.svThemeName"
        :value="theme.themeId.toString()"
        :tabid="`tab-${theme.themeId}`"
        :ariacontrols="`panel-${theme.themeId}`"
        @click="selectTab(theme.themeId.toString())"
      ></pn-tab>
    </pn-tablist>
    <div
      v-for="theme in themes"
      :key="theme.themeId"
      :id="`panel-${theme.themeId}`"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="`tab-${theme.themeId}`"
      v-show="theme.themeId.toString() === activeTabId"
      class="flex flex-direction-column gap-16"
    >
      <CategoryCard
        v-for="category in theme.categories"
        :key="category.categoryId"
        :category="category"
        :theme-id="theme.themeId"
        :depth="1"
      />
      <div class="flex">
        <pn-button
          :icon="plus_small"
          appearance="light"
          variant="borderless"
          left-icon="true"
          @click="openAddCategoryModal(theme.themeId)"
        >
          Add category
        </pn-button>
      </div>
    </div>
    <AddCategoryModal
      :open="showAddCategoryModal"
      :parent-category-id="null"
      :theme-id="selectedThemeId"
      @on-close="showAddCategoryModal = false"
      @on-save="showAddCategoryModal = false"
    />
  </div>
</template>

<style scoped>
.tag-management-view {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.bottom-toast {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  max-width: calc(100vw - 32px);
  text-align: center;
  z-index: 1000;
}
</style>
